import { useEffect, useState } from "react";
import mapServerExportMonitor from "../utils/mapServerExportMonitor";

/** @type {import("../utils/mapServerExportMonitor").MapServerExportMonitor} */
const monitor = mapServerExportMonitor;

/**
 * React hook that exposes MapServer/export monitor state to components.
 *
 * Provides network activity tracking for `/MapServer/export` requests:
 * - Pending request count
 * - Real failures (HTTP 4xx/5xx, timeouts, network errors)
 * - Ignores expected cancellations (zoom/pan aborts)
 *
 * @typedef {import("../utils/mapServerExportMonitor").MapServerExportError} MapServerExportError
 *
 * @returns {{
 *   exportPendingCount: number - Number of pending MapServer/export requests,
 *   exportError: MapServerExportError | null - Last export error (null if none),
 *   clearExportError: () => void - Manually clear the current export error,
 *   formatExportErrorForTooltip: (err: MapServerExportError | null) => string - Format error for display
 * }}
 */
export default function useMapServerExportMonitor() {
  const [state, setState] = useState(() => monitor.getState());

  useEffect(() => {
    const unsubscribe = monitor.subscribe(setState);
    return () => unsubscribe();
  }, []);

  return {
    exportPendingCount: state.exportPendingCount,
    exportError: state.exportError,
    clearExportError: monitor.clearExportError,
    formatExportErrorForTooltip: monitor.formatExportErrorForTooltip,
  };
}
